import { useState } from 'react';
import type { ReportFreshness } from '@/api/types';
import { usePhone } from '@/lib/responsive';
import { ResponsivePicker } from './responsive-picker';
import { fmtDateTime, toInstant } from '@/lib/datetime';
import type { ReportingDataset } from './reporting-freshness';

type ReportingAsOfFreshness = Pick<
  ReportFreshness,
  'delivery_as_of' | 'attribution_as_of'
>;

export type ReportingDataAgeSeverity = 'warning' | 'critical' | null;

/** The visible timestamp contract shared by all four Reporting routes. */
export function reportingDataAsOf(
  freshness: ReportingAsOfFreshness | undefined,
  dataset: ReportingDataset = 'performance',
): string {
  const labels: string[] = [];
  if (dataset !== 'attribution' && freshness?.delivery_as_of) {
    labels.push(`Delivery ${fmtDateTime(freshness.delivery_as_of)} ET`);
  }
  if (dataset !== 'delivery' && freshness?.attribution_as_of) {
    labels.push(`Attribution ${fmtDateTime(freshness.attribution_as_of)} ET`);
  }
  return labels.length ? `Data as of · ${labels.join(' · ')}` : 'Data as of unavailable';
}

/**
 * Age the oldest fact family shown in the combined data-through stamp. This is
 * deliberately separate from the API's three-hour operational staleness flag:
 * the UI warning is a client-reporting threshold of 24/48 elapsed hours.
 */
export function reportingDataAgeSeverity(
  freshness: ReportingAsOfFreshness | undefined,
  dataset: ReportingDataset = 'performance',
  nowMs = Date.now(),
): ReportingDataAgeSeverity {
  const applicable = [
    dataset !== 'attribution' ? freshness?.delivery_as_of : null,
    dataset !== 'delivery' ? freshness?.attribution_as_of : null,
  ]
    .filter((value): value is NonNullable<typeof value> => value != null)
    .map((value) => toInstant(value).getTime())
    .filter((value) => Number.isFinite(value));
  if (!applicable.length) return null;

  const ageMs = Math.max(0, nowMs - Math.min(...applicable));
  if (ageMs > 48 * 60 * 60 * 1_000) return 'critical';
  if (ageMs > 24 * 60 * 60 * 1_000) return 'warning';
  return null;
}

export function ReportingDataControls({
  freshness,
  dataset = 'performance',
  highlightAge = false,
}: {
  freshness: ReportFreshness | undefined;
  dataset?: ReportingDataset;
  highlightAge?: boolean;
}) {
  const ageSeverity = highlightAge
    ? reportingDataAgeSeverity(freshness, dataset)
    : null;
  const ageNotice = ageSeverity === 'critical'
    ? 'Report data is more than 48 hours old.'
    : ageSeverity === 'warning'
      ? 'Report data is more than 24 hours old.'
      : undefined;
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const asOf = reportingDataAsOf(freshness, dataset);

  // The phone header has no room for the two-line stamp. A chip carries the
  // newest stamp and the age colour; tapping it shows the full text.
  if (phone) {
    const stamps = asOf.replace(/^Data as of( · )?/, '');
    const [first, ...rest] = stamps.split(' · ');
    // The chip drops the dataset word and the year ("Sep 10, 2026 at 8:00 AM ET"
    // becomes "Sep 10, 8:00 AM ET"); the sheet behind it has every stamp in full.
    const chipText = first === 'unavailable'
      ? 'Data as of unavailable'
      : `Data as of ${(first ?? '')
          .replace(/^(Delivery|Attribution) /, '')
          .replace(/,? \d{4},?( at)?/, ',')
          .replace(/,\s*,/, ',')}`;
    return (
      <div className="report-data-controls report-data-controls--phone">
        <button
          type="button"
          className={`report-freshness-chip${ageSeverity ? ` report-freshness-chip--${ageSeverity}` : ''}`}
          data-age-status={highlightAge ? ageSeverity ?? 'current' : undefined}
          aria-label={ageNotice ? `${asOf} ${ageNotice}` : asOf}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen(true)}
        >
          <svg className="report-freshness-clock" width="13" height="13" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true"><circle cx="10" cy="10" r="7"/><path d="M10 6v4l3 2"/></svg>
          {chipText}
          <span className="report-freshness-dot" aria-hidden="true"></span>
          {rest.length > 0 && <span className="report-freshness-more" aria-hidden="true">+{rest.length}</span>}
        </button>
        {open && (
          <ResponsivePicker kind="confirmation" label="Data freshness" onClose={() => setOpen(false)}>
            <div className="panel report-freshness-detail">
              <div className="field-lbl">Data as of</div>
              {first === 'unavailable'
                ? <p>Freshness is unavailable for this report.</p>
                : stamps.split(' · ').map((stamp) => <p key={stamp}>{stamp}</p>)}
              {ageNotice && <p className={`report-freshness-notice report-freshness-notice--${ageSeverity}`}>{ageNotice}</p>}
              <button type="button" className="btn" onClick={() => setOpen(false)}>Done</button>
            </div>
          </ResponsivePicker>
        )}
      </div>
    );
  }

  return (
    <div className="report-data-controls">
      <span
        className={`report-data-as-of${ageSeverity ? ` report-data-as-of--${ageSeverity}` : ''}`}
        data-age-status={highlightAge ? ageSeverity ?? 'current' : undefined}
        aria-label={ageNotice
          ? `${reportingDataAsOf(freshness, dataset)} ${ageNotice}`
          : undefined}
        title={ageNotice}
      >
        {reportingDataAsOf(freshness, dataset)}
      </span>
    </div>
  );
}
